/**
 * Pure camera-playback selection for 3D model keyframes. Framework-agnostic (no
 * React / DOM) so it can be unit tested and produces identical motion in both
 * hosts. Ported verbatim from the admin app.
 */

import type { CameraKeyframe, Hotspot3DLabel } from '../schema';

export interface CameraPose {
  cameraOrbit: string;
  cameraTarget?: string;
  fieldOfView?: string;
}

export interface CameraState {
  pose: CameraPose;
  activeLabels: Hotspot3DLabel[];
  transitionMs: number;
  activeKeyframeId: string | null;
}

export const DEFAULT_KEYFRAME_TRANSITION_MS = 300;
export const DEFAULT_RETURN_TRANSITION_MS = 600;
export const DEFAULT_LOOKAHEAD_MS = 5000;

function triggerOf(kf: CameraKeyframe): number {
  return kf.triggerMs ?? 0;
}

/**
 * Labels visible at `nowMs`, across ALL of an element's keyframes. Each label
 * shows from its keyframe's `triggerMs` until its explicit `endMs` or — absent —
 * the end of that keyframe's camera dwell.
 */
export function selectActiveLabels(
  keyframes: CameraKeyframe[],
  nowMs: number,
  lookaheadMs: number = DEFAULT_LOOKAHEAD_MS,
): Hotspot3DLabel[] {
  if (!keyframes || keyframes.length === 0) return [];
  const sorted = [...keyframes].sort((a, b) => triggerOf(a) - triggerOf(b));
  const out: Hotspot3DLabel[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const kf = sorted[i];
    const start = triggerOf(kf);
    const nextTrigger = i + 1 < sorted.length ? triggerOf(sorted[i + 1]) : Number.POSITIVE_INFINITY;
    const holdEnd = kf.endMs != null ? kf.endMs : Math.min(start + lookaheadMs, nextTrigger);
    for (const label of kf.labels ?? []) {
      const end = label.endMs != null ? label.endMs : holdEnd;
      if (nowMs >= start && nowMs < end) out.push(label);
    }
  }
  return out;
}

/**
 * Label-driven camera focus: the pose to frame at `nowMs`, or `null` to center
 * the model. A keyframe WITH labels is in focus while one of its labels is
 * visible; a keyframe WITHOUT labels is in focus during its dwell.
 */
export function selectLabelFocus(
  keyframes: CameraKeyframe[],
  nowMs: number,
  lookaheadMs: number = DEFAULT_LOOKAHEAD_MS,
): { pose: CameraPose; transitionMs: number } | null {
  if (!keyframes || keyframes.length === 0) return null;
  const sorted = [...keyframes].sort((a, b) => triggerOf(a) - triggerOf(b));
  let focus: { pose: CameraPose; transitionMs: number } | null = null;
  for (let i = 0; i < sorted.length; i++) {
    const kf = sorted[i];
    const start = triggerOf(kf);
    const nextTrigger = i + 1 < sorted.length ? triggerOf(sorted[i + 1]) : Number.POSITIVE_INFINITY;
    const holdEnd = kf.endMs != null ? kf.endMs : Math.min(start + lookaheadMs, nextTrigger);
    const labels = kf.labels ?? [];
    const inFocus =
      labels.length > 0
        ? labels.some((l) => nowMs >= start && nowMs < (l.endMs != null ? l.endMs : holdEnd))
        : nowMs >= start && nowMs < holdEnd;
    if (inFocus) {
      focus = {
        pose: { cameraOrbit: kf.cameraOrbit, cameraTarget: kf.cameraTarget, fieldOfView: kf.fieldOfView },
        transitionMs: kf.transitionMs ?? DEFAULT_KEYFRAME_TRANSITION_MS,
      };
    }
  }
  return focus;
}

/**
 * Full camera state at `nowMs` — a keyframe, once fired, holds its pose for a
 * dwell window and then drifts home if nothing follows soon.
 */
export function selectCameraState(
  keyframes: CameraKeyframe[],
  defaultPose: CameraPose,
  nowMs: number,
  lookaheadMs: number = DEFAULT_LOOKAHEAD_MS,
): CameraState {
  const rest: CameraState = {
    pose: defaultPose,
    activeLabels: [],
    transitionMs: DEFAULT_RETURN_TRANSITION_MS,
    activeKeyframeId: null,
  };

  if (!keyframes || keyframes.length === 0) return rest;

  const sorted = [...keyframes].sort((a, b) => triggerOf(a) - triggerOf(b));

  let active: CameraKeyframe | null = null;
  let next: CameraKeyframe | null = null;
  for (const kf of sorted) {
    if (triggerOf(kf) <= nowMs) active = kf;
    else {
      next = kf;
      break;
    }
  }

  if (!active) return rest;

  const nextMs = next ? triggerOf(next) : Number.POSITIVE_INFINITY;
  const holdUntil =
    active.endMs != null ? active.endMs : Math.min(triggerOf(active) + lookaheadMs, nextMs);

  if (nowMs >= holdUntil) return rest;

  return {
    pose: {
      cameraOrbit: active.cameraOrbit,
      cameraTarget: active.cameraTarget,
      fieldOfView: active.fieldOfView,
    },
    activeLabels: active.labels ?? [],
    transitionMs: active.transitionMs ?? DEFAULT_KEYFRAME_TRANSITION_MS,
    activeKeyframeId: active.id,
  };
}
